import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { SelectedPoint } from '@/components/map/MapCanvas'
import Button from '@/components/common/Button'
import NumberField from '@/components/common/NumberField'
import { formatMapId } from '@/common/routeUtil'
import type { SlotMovePlan } from '@/common/slotMoveUtil'
import FloatingPanel from './FloatingPanel'

interface SlotBulkEditPanelProps {
  points: SelectedPoint[]
  pos: { x: number; y: number }
  onMove: (pos: { x: number; y: number }) => void
  onClose: () => void
  isSaving: boolean
  /** 編集セッションに積まれた高さ変更（slotId → mm）。表示に使う */
  pendingHeights: Map<string, number>
  /** 選択中の荷役スロットの高さ変更をセッションへ積む */
  onStageHeight: (heightMm: number) => void
  // --- 一括平行移動（moveOffset が null のときはセクション非表示） ---
  moveOffset?: { x: number; y: number } | null
  movePlan?: SlotMovePlan | null
  /** Δ入力の確定（移動としてセッションへ積まれ、オフセットは 0 に戻る） */
  onMoveOffsetChange?: (offset: { x: number; y: number }) => void
}

// スロット ID（*r<mapId>/rec-N）を「日時 / スロット名」の表示用ラベルにする
function slotLabel(id: string): string {
  const m = id.match(/^\*r([^/]+)\/(.+)$/)
  if (!m) return id
  return `${formatMapId(m[1])} / ${m[2]}`
}

// 複数選択したスロットの一括編集フローティングパネル。
// 高さ変更・平行移動はマップ編集セッションへ積まれるだけで即保存されず、
// マップ下部の「まとめて保存」バーから一度に機体へ保存する。
export default function SlotBulkEditPanel({
  points,
  pos,
  onMove,
  onClose,
  isSaving,
  pendingHeights,
  onStageHeight,
  moveOffset = null,
  movePlan = null,
  onMoveOffsetChange,
}: SlotBulkEditPanelProps) {
  const { t } = useTranslation()
  const cargoPoints = useMemo(() => points.filter((p) => p.isCargo), [points])

  const hasMoveOffset =
    moveOffset !== null && (moveOffset.x !== 0 || moveOffset.y !== 0)
  // 高さ入力の初期表示: 先頭の荷役スロットのステージ済み値（無ければ現在値）
  const firstCargo = cargoPoints[0]
  const displayHeightMm = firstCargo
    ? (pendingHeights.get(firstCargo.id) ??
      Math.floor(Math.max(firstCargo.height ?? 0, 0) * 1000))
    : 0

  return (
    <FloatingPanel
      title={t('scenarios.slotBulkEditTitle')}
      pos={pos}
      onMove={onMove}
      onClose={onClose}
      width="w-72"
    >
      {/* 内容が長いときはパネル内でスクロールし、下端が画面外に隠れないようにする */}
      <div className="p-2.5 space-y-2 text-xs max-h-[60vh] overflow-y-auto">
        <div className="text-gray-600">
          {t('scenarios.selectedSlotCount', { count: points.length })}
        </div>

        {/* 選択スロットの一覧（荷役スロットは現在の高さ、それ以外は対象外表示） */}
        <ul className="max-h-40 overflow-y-auto border rounded divide-y">
          {points.map((p) => {
            const staged = p.isCargo ? pendingHeights.get(p.id) : undefined
            return (
              <li
                key={p.id}
                className="flex items-center justify-between gap-2 px-2 py-1"
              >
                <span className="truncate text-gray-800" title={p.id}>
                  {slotLabel(p.id)}
                </span>
                {p.isCargo ? (
                  <span className="flex items-center gap-1 flex-shrink-0">
                    <span className="px-1 py-0.5 text-[10px] font-bold text-white bg-orange-500 rounded leading-none">
                      高
                    </span>
                    <span className="tabular-nums text-orange-600">
                      {Math.floor(Math.max(p.height ?? 0, 0) * 1000)}
                      {/* セッションに積まれた高さ変更は「現在値 → 新値」で示す */}
                      {staged !== undefined && ` → ${staged}`} mm
                    </span>
                  </span>
                ) : (
                  <span className="flex-shrink-0 text-[10px] text-gray-400">
                    {t('scenarios.bulkNotTarget')}
                  </span>
                )}
              </li>
            )
          })}
        </ul>

        {/* 一括平行移動（記録ルートの端点スロットのみ対象）。
            ドラッグ / Δ入力確定でセッションへ積まれ、オフセットは 0 に戻る */}
        {moveOffset && (
          <div className="border-t pt-2">
            <span className="block text-[11px] text-gray-500 mb-0.5">
              {t('scenarios.bulkMoveLabel')}
            </span>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="block text-[11px] text-gray-500 mb-0.5">
                  Δx (m)
                </span>
                <NumberField
                  value={Number(moveOffset.x.toFixed(3))}
                  step={0.05}
                  disabled={isSaving}
                  onCommit={(v) =>
                    onMoveOffsetChange?.({ x: v, y: moveOffset.y })
                  }
                />
              </label>
              <label className="block">
                <span className="block text-[11px] text-gray-500 mb-0.5">
                  Δy (m)
                </span>
                <NumberField
                  value={Number(moveOffset.y.toFixed(3))}
                  step={0.05}
                  disabled={isSaving}
                  onCommit={(v) =>
                    onMoveOffsetChange?.({ x: moveOffset.x, y: v })
                  }
                />
              </label>
            </div>
            <div className="mt-0.5 text-[10px] text-gray-400">
              {t('scenarios.bulkMoveHint')}
            </div>
            {hasMoveOffset && movePlan?.ok === true && (
              <div className="mt-1 text-[10px] text-gray-400">
                {t('scenarios.bulkMoveNote', {
                  slots: movePlan.pointCount,
                  routes: movePlan.jobs.length,
                })}
              </div>
            )}
            {movePlan && !movePlan.ok && movePlan.reason !== 'noTarget' && (
              <div className="mt-1 text-[10px] text-red-600">
                {t(
                  movePlan.reason === 'validation'
                    ? 'scenarios.bulkMoveInvalid'
                    : 'scenarios.bulkMoveExtractError',
                  { mapId: formatMapId(movePlan.mapId) }
                )}
              </div>
            )}
            {hasMoveOffset && (
              <Button
                variant="white"
                size="sm"
                disabled={isSaving}
                onClick={() => onMoveOffsetChange?.({ x: 0, y: 0 })}
                className="mt-1.5 w-full"
              >
                {t('scenarios.bulkMoveReset')}
              </Button>
            )}
          </div>
        )}

        {/* 高さの一括変更（荷役スロットのみ対象）。値はセッションへ積むだけ */}
        {cargoPoints.length > 0 ? (
          <div className="border-t pt-2">
            <span className="block text-[11px] text-gray-500 mb-0.5">
              {t('scenarios.forkHeight')} (mm)
            </span>
            <NumberField
              value={displayHeightMm}
              step={10}
              min={0}
              disabled={isSaving}
              onCommit={(v) => onStageHeight(Math.max(0, Math.floor(v)))}
            />
            <div className="mt-0.5 text-[10px] text-gray-400">
              {t('scenarios.bulkCargoNote', {
                cargo: cargoPoints.length,
                total: points.length,
              })}
            </div>
          </div>
        ) : (
          <div className="text-[11px] text-gray-400">
            {t('scenarios.bulkNoCargo')}
          </div>
        )}

        <div className="text-[10px] text-gray-400">
          {t('scenarios.bulkPendingNote')}
        </div>
      </div>
    </FloatingPanel>
  )
}
